import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SherlockCorrectionSettings } from "@/components/SherlockCorrectionSettings";
import { RoutingEngineStatus } from "@/components/RoutingEngineStatus";
import { WaForwardRoutes } from "@/components/WaForwardRoutes";
import { Save, ShieldCheck, ExternalLink, Calendar, CheckCircle2, XCircle, Upload, Database, BarChart3, Brain, PlayCircle, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

function StatBox({ label, value, color = "zinc" }) {
  const cls = {
    zinc:    "border-zinc-200 bg-zinc-50 text-zinc-800",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    rose:    "border-rose-300 bg-rose-50 text-rose-800",
  }[color] || "border-zinc-200 bg-zinc-50 text-zinc-800";
  return (
    <div className={`border rounded-sm px-2 py-1.5 ${cls}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-sm font-bold font-mono">{value}</div>
    </div>
  );
}

export default function SettingsPage() {
  const [s, setS] = useState({
    mode:"simulator",
    phone_number_id:"", access_token:"", app_secret:"", verify_token:"",
    business_hours_start:"08:30", business_hours_end:"17:00",
  });
  const [hasStoredToken, setHasStoredToken] = useState(false);
  const [hasStoredSecret, setHasStoredSecret] = useState(false);
  const [status, setStatus] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [gStatus, setGStatus] = useState(null);
  const [gCreds, setGCreds] = useState({ client_id: "", client_secret: "" });
  const [showGSecret, setShowGSecret] = useState(false);
  const [icalStats, setIcalStats] = useState(null);
  const [icalFile, setIcalFile] = useState(null);
  const [icalUploading, setIcalUploading] = useState(false);
  const [icalProgress, setIcalProgress] = useState(0);
  const [icalResult, setIcalResult] = useState(null);
  const [icalLearning, setIcalLearning] = useState(false);
  const [icalLearnResult, setIcalLearnResult] = useState(null);

  // v85.25 — AI WO Arranger state
  const [aiCfg, setAiCfg] = useState({
    enabled: false, openrouter_model: "", system_prompt: "",
    updated_at: null, updated_by: null,
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPromptDraft, setAiPromptDraft] = useState("");
  const [aiRunFrom, setAiRunFrom] = useState("");
  const [aiRunTo, setAiRunTo] = useState("");
  const [aiRunning, setAiRunning] = useState(false);
  const [aiRunResult, setAiRunResult] = useState(null);
  const [aiLogs, setAiLogs] = useState([]);

  // v85.25 — Backfill lat/lng WO lama (batching dgn progres bar)
  const [bfRunning, setBfRunning] = useState(false);
  const [bfStop, setBfStop] = useState(false);
  const [bfStats, setBfStats] = useState({
    rounds: 0, scanned: 0, from_link: 0, from_addr: 0,
    rejected: 0, failed: 0, dry_run: false,
  });
  const [bfDryRun, setBfDryRun] = useState(false);

  // v85.26 — Cleanup stale SCHEDULED → DONE
  const [cuDays, setCuDays] = useState(3);
  const [cuBusy, setCuBusy] = useState(false);
  const [cuResult, setCuResult] = useState(null);

  // v85.30 — Backfill "Ket: jam HH.MM" dari pesan konfirmasi utk WO QO lama
  const _today = new Date();
  const _plus = (n) => {
    const d = new Date(_today); d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const [qtFrom, setQtFrom] = useState(_plus(1));  // besok
  const [qtTo,   setQtTo]   = useState(_plus(3));  // +3 hari
  const [qtDryRun, setQtDryRun] = useState(true);
  const [qtBusy, setQtBusy] = useState(false);
  const [qtResult, setQtResult] = useState(null);

  async function runQoTimeBackfill() {
    if (!qtFrom || !qtTo) { toast.error("Pilih tanggal dulu"); return; }
    setQtBusy(true); setQtResult(null);
    try {
      const r = await api.backfillQoTimeHint({
        date_from: qtFrom, date_to: qtTo, dry_run: qtDryRun,
      });
      setQtResult(r);
      if (qtDryRun) {
        toast.success(`Preview: ${r.extracted}/${r.qo_missing_hint} WO berhasil diekstrak jam-nya`);
      } else {
        toast.success(`✓ ${r.updated} WO di-update dgn "Ket: jam ..."`);
      }
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    } finally { setQtBusy(false); }
  }

  async function runCleanupStale(dry) {
    setCuBusy(true);
    try {
      const r = await api.cleanupStaleScheduled({ days_threshold: cuDays, dry_run: dry });
      setCuResult(r);
      if (dry) {
        toast.success(`Dry-run: ${r.matched} WO akan di-update`);
      } else {
        toast.success(`✓ ${r.modified} WO di-set DONE`);
      }
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    } finally { setCuBusy(false); }
  }

  async function runBackfillLoop() {
    setBfRunning(true);
    setBfStop(false);
    setBfStats({ rounds: 0, scanned: 0, from_link: 0, from_addr: 0,
                 rejected: 0, failed: 0, dry_run: bfDryRun });
    let stopFlag = false;
    const update = (patch) => setBfStats(s => ({ ...s, ...patch }));
    try {
      while (!stopFlag) {
        // Guard pakai state via functional read supaya reflect user Stop click
        // selama loop berjalan.
        if (bfStop) break;
        const d = await api.backfillWoCoords({ dry_run: bfDryRun, limit: 40 });
        setBfStats(s => ({
          rounds: s.rounds + 1,
          scanned: s.scanned + (d.scanned || 0),
          from_link: s.from_link + (d.resolved_from_link || 0),
          from_addr: s.from_addr + (d.resolved_from_address || 0),
          rejected: s.rejected + (d.rejected_out_of_area || 0),
          failed: s.failed + (d.failed || 0),
          dry_run: bfDryRun,
        }));
        // Stop kriteria:
        //  - scanned=0 (query sudah bersih)
        //  - dry_run (hanya 1x round, ngga ada perubahan DB)
        //  - semua hasil rejected/failed (ngga ada updateable → next loop sama hasil)
        if ((d.scanned || 0) === 0) break;
        if (bfDryRun) break;
        if (((d.resolved_from_link || 0) + (d.resolved_from_address || 0)) === 0) {
          // Tidak ada WO yg berhasil di-update di round ini → sisanya tidak akan
          // berubah tanpa intervensi (admin edit maps_link / address manual).
          break;
        }
        // Yield supaya UI responsive
        await new Promise(r => setTimeout(r, 400));
        // Baca state terbaru utk Stop
        // eslint-disable-next-line no-loop-func
        setBfStop(curr => { stopFlag = curr; return curr; });
      }
      toast.success(bfDryRun ? "Preview selesai" : "Backfill selesai");
      // refresh page info kalau perlu
    } catch (e) {
      toast.error("Backfill gagal: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBfRunning(false);
    }
  }

  async function loadAiArranger() {
    try {
      const c = await api.aiArrangerConfigGet();
      setAiCfg(c);
      setAiPromptDraft(c.system_prompt || "");
    } catch (e) { /* ignore */ }
    try {
      const l = await api.aiArrangerLogs(20);
      setAiLogs(l.items || []);
    } catch (e) { /* ignore */ }
  }
  useEffect(() => { loadAiArranger(); }, []);

  async function saveAiToggle(v) {
    setAiBusy(true);
    try {
      const c = await api.aiArrangerConfigPut({ enabled: !!v });
      setAiCfg(c);
      toast.success(`AI WO Arranger ${v ? "DIAKTIFKAN" : "DIMATIKAN"}`);
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  }
  async function saveAiPrompt() {
    setAiBusy(true);
    try {
      const c = await api.aiArrangerConfigPut({ system_prompt: aiPromptDraft });
      setAiCfg(c);
      toast.success("System prompt disimpan");
    } catch (e) { toast.error("Gagal simpan prompt"); }
    finally { setAiBusy(false); }
  }
  async function runAiNow() {
    if (!aiRunFrom) { toast.error("Pilih tanggal dulu"); return; }
    setAiRunning(true);
    setAiRunResult(null);
    try {
      const started = await api.aiArrangerRunNow(aiRunFrom, aiRunTo || aiRunFrom);
      const jobId = started?.job_id;
      if (!jobId) throw new Error("job_id kosong dari server");
      toast.success("Arranger berjalan di background…");
      // Poll setiap 3 detik, max ~10 menit
      const MAX_POLLS = 200;
      let final = null;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(res => setTimeout(res, 3000));
        try {
          const st = await api.aiArrangerRunStatus(jobId);
          if (st?.status === "done" || st?.status === "error") { final = st; break; }
        } catch (_) { /* ignore transient errors */ }
      }
      if (!final) {
        toast.error("Timeout menunggu hasil (10 menit). Coba refresh logs manual.");
        return;
      }
      if (final.status === "error") {
        toast.error("Gagal: " + (final.error || "unknown"));
        return;
      }
      setAiRunResult(final);
      const totalMoved = (final.results || []).reduce((s, x) => s + (x.moved || 0), 0);
      toast.success(`Selesai: ${totalMoved} WO dipindah dari ${final.total_days} hari`);
      const l = await api.aiArrangerLogs(20);
      setAiLogs(l.items || []);
    } catch (e) {
      toast.error("Gagal jalankan: " + (e?.response?.data?.detail || e.message));
    } finally { setAiRunning(false); }
  }

  async function load() {
    setStatus(await api.waStatus());
    try {
      const cfg = await api.waSettings();
      setS({
        mode: cfg.mode === "unconfigured" ? "simulator" : cfg.mode,
        phone_number_id: cfg.phone_number_id || "",
        access_token: "",   // never rehydrate secret
        app_secret: "",     // never rehydrate secret
        verify_token: cfg.verify_token || "",
        business_hours_start: cfg.business_hours_start || "08:30",
        business_hours_end: cfg.business_hours_end || "17:00",
      });
      setHasStoredToken(!!cfg.has_access_token);
      setHasStoredSecret(!!cfg.has_app_secret);
    } catch {}
    setGStatus(await api.googleStatus());
    try { setIcalStats(await api.icalStats()); } catch {}
  }
  useEffect(() => {
    load();
    // Check URL for OAuth redirect result
    const p = new URLSearchParams(window.location.search);
    if (p.get("google") === "connected") toast.success(`Google Calendar terhubung: ${p.get("email")||""}`);
    if (p.get("google") === "error") toast.error(`Google gagal: ${p.get("msg")||""}`);
    if (p.get("google")) window.history.replaceState({},"", window.location.pathname);
  }, []);

  async function save() {
    try { await api.waSave(s); toast.success("Pengaturan disimpan"); load(); }
    catch { toast.error("Gagal simpan"); }
  }

  async function saveGoogleCreds() {
    if (!gCreds.client_id || !gCreds.client_secret) { toast.error("Client ID & Secret wajib"); return; }
    try {
      await api.googleSaveCreds(gCreds);
      toast.success("Credential Google tersimpan");
      load();
    } catch { toast.error("Gagal simpan"); }
  }

  async function connectGoogle() {
    try {
      const r = await api.googleLogin();
      window.location.href = r.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mulai OAuth");
    }
  }

  async function disconnectGoogle() {
    await api.googleDisconnect();
    toast.success("Google Calendar diputus");
    load();
  }

  async function uploadIcal() {
    if (!icalFile) { toast.error("Pilih file .ics atau .zip dulu"); return; }
    setIcalUploading(true); setIcalProgress(0); setIcalResult(null);
    try {
      const r = await api.icalImport(icalFile, (e) => {
        if (e?.total) setIcalProgress(Math.round((e.loaded * 100) / e.total));
      });
      setIcalResult(r);
      toast.success(`Impor selesai: ${r.total_processed} event · ${r.inserted} baru · ${r.updated} update`);
      // Refresh stats
      try { setIcalStats(await api.icalStats()); } catch {}
      setIcalFile(null);
      // Reset native file input
      const fileEl = document.getElementById("ical-file-input");
      if (fileEl) fileEl.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal impor iCal");
    } finally {
      setIcalUploading(false);
    }
  }

  async function learnDurationsIcal() {
    setIcalLearning(true); setIcalLearnResult(null);
    try {
      const r = await api.icalLearn();
      setIcalLearnResult(r);
      const parts = Object.entries(r.by_service_type || {}).map(([k,v]) => `${k}: ${v.toLocaleString("id-ID")}`).join(" · ");
      toast.success(`Belajar selesai — ${r.events_matched.toLocaleString("id-ID")} event · ${r.buckets_saved} bucket · ${parts}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal belajar dari iCal");
    } finally {
      setIcalLearning(false);
    }
  }

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div className="border border-border bg-white rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-4 h-4 text-[#E65100]" />
          <h2 className="font-['Manrope'] font-bold">Status Integrasi WhatsApp</h2>
        </div>
        <div className="grid grid-cols-3 gap-4 text-[13px]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Mode</div>
            <div className="font-mono mt-1">{status?.mode || "…"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Terkonfigurasi</div>
            <div className="font-mono mt-1">{status?.configured ? "YA" : "TIDAK"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Phone ID</div>
            <div className="font-mono mt-1">…{status?.phone_number_id_last4 || "----"}</div>
          </div>
        </div>
        {!status?.configured && (
          <div className="mt-4 text-[12px] p-3 bg-yellow-50 border border-yellow-200 rounded-sm text-yellow-900">
            <b>Belum terhubung ke WhatsApp Cloud API.</b> Sistem berjalan dalam <b>Simulator</b> untuk testing.
            Isi credential di bawah, lalu daftarkan webhook URL di Meta Developer Portal.
          </div>
        )}
      </div>

      <div className="border border-border bg-white rounded-sm p-5">
        <h2 className="font-['Manrope'] font-bold mb-4">Kredensial Meta WhatsApp Cloud API</h2>
        <div className="space-y-3">
          <div className="text-[12px] text-zinc-600 mb-2">
            Dapatkan dari <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="text-[#2962FF] inline-flex items-center gap-1">Meta Developer Portal <ExternalLink className="w-3 h-3" /></a> → App Anda → WhatsApp → API Setup.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Phone Number ID</Label>
              <Input value={s.phone_number_id} onChange={e=>setS({...s, phone_number_id:e.target.value})} className="rounded-sm mt-1" data-testid="wa-phone-id" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Verify Token</Label>
              <Input value={s.verify_token} onChange={e=>setS({...s, verify_token:e.target.value})} className="rounded-sm mt-1" data-testid="wa-verify-token" placeholder="string acak 32+ char" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Access Token (System User Token)
                {hasStoredToken && !s.access_token && (
                  <span className="ml-2 text-emerald-700 normal-case tracking-normal">· tersimpan (kosongkan utk pakai yg lama)</span>
                )}
              </Label>
              <Input type={showSecrets?"text":"password"} value={s.access_token}
                onChange={e=>setS({...s, access_token:e.target.value})}
                placeholder={hasStoredToken ? "•••••••• (biarkan kosong utk tetap pakai yg tersimpan)" : "EAAG..."}
                className="rounded-sm mt-1 font-mono" data-testid="wa-access-token" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                App Secret (untuk HMAC webhook)
                {hasStoredSecret && !s.app_secret && (
                  <span className="ml-2 text-emerald-700 normal-case tracking-normal">· tersimpan (kosongkan utk pakai yg lama)</span>
                )}
              </Label>
              <Input type={showSecrets?"text":"password"} value={s.app_secret}
                onChange={e=>setS({...s, app_secret:e.target.value})}
                placeholder={hasStoredSecret ? "•••••••• (biarkan kosong utk tetap pakai yg tersimpan)" : ""}
                className="rounded-sm mt-1 font-mono" data-testid="wa-app-secret" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Switch checked={showSecrets} onCheckedChange={setShowSecrets} data-testid="show-secrets-toggle" />
            <Label className="text-[12px]">Tampilkan token</Label>
          </div>

          <div className="mt-3 pt-3 border-t border-border">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Mode Operasi</Label>
            <div className="flex items-center gap-3 mt-2">
              {/* Pill-toggle Simulator ↔ Live */}
              <div
                className="inline-flex bg-zinc-100 rounded-sm p-0.5 border border-border"
                role="group"
                aria-label="Mode WhatsApp"
                data-testid="wa-mode-toggle"
              >
                <button
                  type="button"
                  onClick={async () => {
                    if (s.mode === "simulator") return;
                    const next = { ...s, mode: "simulator" };
                    setS(next);
                    try { await api.waSave(next); toast.success("Mode: Simulator"); load(); }
                    catch { toast.error("Gagal ubah mode"); }
                  }}
                  className={`px-4 h-8 text-[12px] rounded-sm font-medium transition-colors ${
                    s.mode === "simulator"
                      ? "bg-white text-[#E65100] shadow-sm border border-[#E65100]/30"
                      : "text-zinc-500 hover:text-zinc-800"
                  }`}
                  data-testid="wa-mode-simulator-btn"
                >
                  Simulator
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (s.mode === "live") return;
                    if (!s.phone_number_id || (!s.access_token && !hasStoredToken)) {
                      toast.error("Isi Phone Number ID & Access Token dulu sebelum masuk LIVE");
                      return;
                    }
                    const next = { ...s, mode: "live" };
                    setS(next);
                    try { await api.waSave(next); toast.success("Mode: LIVE — balasan akan dikirim ke WhatsApp asli"); load(); }
                    catch { toast.error("Gagal ubah mode"); }
                  }}
                  className={`px-4 h-8 text-[12px] rounded-sm font-medium transition-colors ${
                    s.mode === "live"
                      ? "bg-white text-emerald-700 shadow-sm border border-emerald-500/40"
                      : "text-zinc-500 hover:text-zinc-800"
                  }`}
                  data-testid="wa-mode-live-btn"
                >
                  Live
                </button>
              </div>
              <span className="text-[11px] text-zinc-500">
                {s.mode === "live"
                  ? "🟢 Balasan dikirim ke WhatsApp asli"
                  : "🟡 Balasan tetap di simulator (aman untuk test)"}
              </span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-border">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Jam Operasional (WIB)</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <Input type="time" value={s.business_hours_start} onChange={e=>setS({...s, business_hours_start:e.target.value})} className="rounded-sm" data-testid="bh-start" />
              <Input type="time" value={s.business_hours_end} onChange={e=>setS({...s, business_hours_end:e.target.value})} className="rounded-sm" data-testid="bh-end" />
            </div>
          </div>

          <Button onClick={save} className="w-full bg-[#E65100] hover:bg-[#F57C00] rounded-sm mt-2 gap-2" data-testid="save-settings-btn">
            <Save className="w-4 h-4" />Simpan Pengaturan
          </Button>
        </div>
      </div>

      <WaForwardRoutes />


      <div className="border border-border bg-white rounded-sm p-5">
        <h2 className="font-['Manrope'] font-bold mb-3">Webhook URL untuk Meta</h2>
        <div className="text-[12px] text-zinc-600 mb-2">Copy URL ini ke <b>Meta Developer Portal → WhatsApp → Configuration → Webhook → Callback URL</b>. Subscribe field <code className="font-mono bg-zinc-100 px-1">messages</code>.</div>
        <div className="terminal p-3 rounded-sm break-all">{webhookUrl}</div>
      </div>

      {/* Google Calendar */}
      <div className="border border-border bg-white rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-[#2962FF]" />
          <h2 className="font-['Manrope'] font-bold">Google Calendar Sync</h2>
          {gStatus?.connected && <span className="ml-auto flex items-center gap-1 text-[12px] text-emerald-600"><CheckCircle2 className="w-4 h-4" />Terhubung: {gStatus.email}</span>}
          {!gStatus?.connected && <span className="ml-auto flex items-center gap-1 text-[12px] text-zinc-500"><XCircle className="w-4 h-4" />Belum terhubung</span>}
        </div>

        <div className="text-[12px] text-zinc-600 mb-3">
          Sambungkan akun admin Anda supaya jadwal otomatis muncul di 9 kalender teknisi (semua kalender harus sudah di-share ke akun admin dengan permission <b>Make changes to events</b>).
        </div>

        <div className="space-y-3">
          <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">1. Redirect URI (WAJIB paste ke Google Cloud Console)</div>
          <div className="terminal p-2.5 rounded-sm break-all text-[11px] flex items-center gap-2">
            <span className="flex-1">{gStatus?.redirect_uri || "..."}</span>
            <button
              type="button"
              onClick={()=>{navigator.clipboard.writeText(gStatus?.redirect_uri||""); toast.success("URL disalin");}}
              className="text-[10px] bg-white/10 hover:bg-white/20 px-2 py-1 rounded-sm text-[#4ADE80]"
              data-testid="copy-redirect-uri">SALIN</button>
          </div>
          <div className="text-[11px] p-2.5 bg-amber-50 border border-amber-200 rounded-sm text-amber-900">
            <b>⚠️ Penting:</b> Jika Anda pakai aplikasi ini di 2 URL (Preview & Production), <b>daftarkan KEDUA URL</b> di Google Cloud Console → Authorized redirect URIs. Setiap environment akan mengirim URL yang berbeda saat login.
            <div className="mt-1.5 font-mono text-[10.5px] leading-relaxed text-amber-800">
              • https://indonesian-demo.preview.emergentagent.com/api/google/callback<br/>
              • https://indonesian-demo.emergent.host/api/google/callback
            </div>
          </div>

          <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mt-3">2. Isi OAuth Credentials</div>
          <div className="text-[11px] text-zinc-500 -mt-1">Dapatkan di <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-[#2962FF] inline-flex items-center gap-1">Google Cloud Console → Credentials <ExternalLink className="w-3 h-3" /></a></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Client ID</Label>
              <Input value={gCreds.client_id} onChange={e=>setGCreds({...gCreds, client_id:e.target.value})}
                className="rounded-sm mt-1 font-mono text-[12px]" placeholder="xxx.apps.googleusercontent.com" data-testid="g-client-id" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Client Secret</Label>
              <Input type={showGSecret?"text":"password"} value={gCreds.client_secret} onChange={e=>setGCreds({...gCreds, client_secret:e.target.value})}
                className="rounded-sm mt-1 font-mono text-[12px]" placeholder="GOCSPX-…" data-testid="g-client-secret" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={showGSecret} onCheckedChange={setShowGSecret} data-testid="show-g-secret" />
            <Label className="text-[12px]">Tampilkan secret</Label>
            <Button onClick={saveGoogleCreds} variant="outline" className="ml-auto rounded-sm" data-testid="g-save-creds">Simpan Credentials</Button>
          </div>

          <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mt-3">3. Login dengan akun admin</div>
          <div className="flex items-center gap-2">
            {!gStatus?.connected ? (
              <Button onClick={connectGoogle} disabled={!gStatus?.has_credentials}
                className="bg-[#2962FF] hover:bg-[#2979FF] rounded-sm gap-2" data-testid="g-connect-btn">
                <Calendar className="w-4 h-4" />
                {gStatus?.has_credentials ? "Login dengan Google Admin" : "Simpan credentials dulu"}
              </Button>
            ) : (
              <>
                <div className="flex-1 text-[13px]">Terhubung sebagai <b>{gStatus.email}</b></div>
                <Button variant="outline" onClick={disconnectGoogle} className="rounded-sm" data-testid="g-disconnect-btn">Disconnect</Button>
              </>
            )}
          </div>

          {gStatus?.connected && (
            <div className="pt-3 mt-3 border-t border-border text-[12px] text-zinc-600 space-y-2">
              {gStatus?.needs_reconnect && (
                <div className="p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900" data-testid="g-scope-warning">
                  <div className="font-semibold text-[12.5px] mb-1">⚠️ Google perlu Re-connect</div>
                  <div className="text-[11.5px] leading-snug">
                    Beberapa izin Google belum aktif untuk akun ini
                    {gStatus.missing_scopes?.length ? (
                      <> — kurang: <b>{gStatus.missing_scopes.join(", ")}</b>.</>
                    ) : <>. Scope belum ter-verifikasi (token lama).</>}
                    <br/>Akibatnya <b>backup Sheets, upload foto ke Drive, atau kirim slip gaji via Gmail</b> tidak jalan
                    (slip tetap terkirim lewat email cadangan Emergent).
                    Klik <b>Disconnect</b> lalu <b>Login</b> ulang untuk memberi izin baru.
                  </div>
                  <Button variant="outline" size="sm" onClick={disconnectGoogle}
                    className="rounded-sm mt-2 border-amber-400 text-amber-900 hover:bg-amber-100"
                    data-testid="g-reconnect-btn">
                    Disconnect & Re-connect Google
                  </Button>
                </div>
              )}
              <div data-testid="g-gmail-slip-status">
                {gStatus?.scopes_ok?.gmail
                  ? <>✅ Slip gaji dikirim dari Gmail <b>{gStatus.email}</b> (cadangan: email Emergent).</>
                  : <>⚠️ Izin Gmail belum aktif — slip gaji sementara dikirim lewat email Emergent.</>}
              </div>
              <div>✅ Selanjutnya buka menu <b>Teknisi</b> → pilih kalender untuk masing-masing teknisi supaya jadwal otomatis nyambung ke kalender mereka.</div>
              <Button variant="outline" onClick={async ()=>{
                try {
                  const r = await api.googleLearn();
                  toast.success(`${r.learned} durasi aktual dipelajari dari ${r.events_scanned} event · ${r.technicians_scanned} teknisi`);
                } catch { toast.error("Gagal belajar"); }
              }} className="rounded-sm gap-1.5" data-testid="g-learn-btn">
                <Calendar className="w-3.5 h-3.5" /> Belajar Durasi dari History Google Calendar (60 hari terakhir)
              </Button>

              <Button variant="outline" onClick={async ()=>{
                const tid = toast.loading("Menyinkron baris Order-ID ke semua event…");
                let scannedTotal=0, patchedTotal=0, skippedTotal=0, errTotal=0;
                try {
                  for (let i = 0; i < 100; i++) {
                    const r = await api.refreshOrderIdLines(30);
                    scannedTotal += r.scanned;
                    patchedTotal += r.patched;
                    skippedTotal += r.skipped;
                    errTotal += (r.errors || []).length;
                    if (!r.needs_more) break;
                    toast.loading(`Sinkron Order-ID… ${scannedTotal} diproses, sisa ${r.remaining}`, {id: tid});
                  }
                  toast.success(
                    `Selesai: ${patchedTotal} event di-refresh Order-ID, ${skippedTotal} sudah benar, ${errTotal} error.`,
                    {id: tid, duration: 10000}
                  );
                } catch (e) { toast.error("Gagal: " + (e?.response?.data?.detail || ""), {id: tid}); }
              }} className="rounded-sm gap-1.5" data-testid="g-refresh-orderid-btn">
                <Calendar className="w-3.5 h-3.5" /> Sinkronkan Order-ID Lama ke ID Terbaru di Semua Event
              </Button>

              <Button variant="outline" onClick={async ()=>{
                if (!window.confirm("Ini akan memasukkan Keterangan admin (kerusakan / permintaan jam / dll) ke deskripsi Google Calendar event untuk semua WO mulai hari ini ke depan yang belum tersinkron. Lanjut?")) return;
                const tid = toast.loading("Memasukkan Keterangan ke Google Calendar…");
                let scannedTotal = 0, patchedTotal = 0, errTotal = 0;
                try {
                  // Loop paginated sampai selesai (max 100 batch)
                  for (let i = 0; i < 100; i++) {
                    const r = await api.backfillGcalNotes(30);
                    scannedTotal += r.scanned;
                    patchedTotal += r.patched;
                    errTotal += (r.errors || []).length;
                    if (!r.needs_more) break;
                    toast.loading(`Memasukkan Keterangan… ${scannedTotal} diproses, sisa ${r.remaining}`, {id: tid});
                  }
                  toast.success(
                    `Selesai: ${patchedTotal} event dapat Keterangan baru, ${errTotal} error.`,
                    {id: tid, duration: 10000}
                  );
                } catch (e) { toast.error("Gagal: " + (e?.response?.data?.detail || ""), {id: tid}); }
              }} className="rounded-sm gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50" data-testid="g-backfill-notes-btn">
                <Calendar className="w-3.5 h-3.5" /> Kirim Keterangan Admin ke Google Calendar (WO mulai hari ini)
              </Button>

              <Button variant="outline" onClick={async ()=>{
                if (!window.confirm("Ini akan MENGHAPUS Work Order duplikat (berdasarkan google_event_id). Data historis di GCal tidak disentuh. Lanjut?")) return;
                const tid = toast.loading("Mendeteksi duplikat & apply unique index…");
                try {
                  const r = await api.dedupWoByGcalEvent(false);
                  toast.success(
                    `Ditemukan ${r.duplicate_groups} grup dup · Dihapus ${r.deleted} WO · Unique index: ${r.unique_index_applied ? "OK ✅" : "GAGAL ❌ " + (r.unique_index_error||"")}`,
                    {id: tid, duration: 12000}
                  );
                } catch (e) { toast.error("Gagal: " + (e?.response?.data?.detail || ""), {id: tid}); }
              }} className="rounded-sm gap-1.5 border-red-200 text-red-700 hover:bg-red-50" data-testid="g-dedup-wo-btn">
                <Calendar className="w-3.5 h-3.5" /> Hapus WO Duplikat & Terapkan Unique Index
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ─────────── Import iCal (.ics / .zip) ─────────── */}
      <div className="border border-border bg-white rounded-sm p-5" data-testid="ical-import-panel">
        <div className="flex items-center gap-2 mb-1">
          <Database className="w-4 h-4 text-[#7C3AED]" />
          <h2 className="font-['Manrope'] font-bold">Impor Kalender iCal (.ics / .zip)</h2>
        </div>
        <p className="text-[12px] text-zinc-600 mb-4">
          Upload file <b>.ics</b> atau <b>.zip</b> (berisi .ics) hasil ekspor Google Calendar.
          Event akan disimpan ke koleksi <span className="font-mono text-[11px] bg-zinc-100 px-1 rounded-sm">calendar_events</span> dan
          dapat dicari AI berdasarkan nomor HP di deskripsi event.
        </p>

        {/* Stats ringkas */}
        {icalStats && (
          <div className="grid grid-cols-3 gap-3 mb-4 text-[12px]">
            <div className="bg-zinc-50 border border-border rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Event</div>
              <div className="font-mono text-lg mt-1" data-testid="ical-total-events">{icalStats.total_events?.toLocaleString("id-ID") ?? 0}</div>
            </div>
            <div className="bg-zinc-50 border border-border rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Punya Nomor HP</div>
              <div className="font-mono text-lg mt-1">{icalStats.events_with_phone?.toLocaleString("id-ID") ?? 0}</div>
            </div>
            <div className="bg-zinc-50 border border-border rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sumber File</div>
              <div className="font-mono text-lg mt-1">{icalStats.sources?.length ?? 0}</div>
            </div>
          </div>
        )}

        {icalStats?.sources?.length > 0 && (
          <div className="mb-4 text-[11px] text-zinc-500 space-y-0.5">
            <div className="uppercase tracking-widest text-[10px] mb-1">File terimpor:</div>
            {icalStats.sources.slice(0, 8).map((src, i) => (
              <div key={i} className="font-mono truncate">· {src}</div>
            ))}
            {icalStats.sources.length > 8 && <div>· … dan {icalStats.sources.length - 8} lainnya</div>}
          </div>
        )}

        {/* Upload form */}
        <div className="flex items-center gap-2 flex-wrap">
          <input
            id="ical-file-input"
            type="file"
            accept=".ics,.ical,.zip"
            onChange={(e) => setIcalFile(e.target.files?.[0] || null)}
            className="text-[12px] file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border file:border-border file:bg-white file:text-[12px] file:cursor-pointer hover:file:bg-zinc-50"
            data-testid="ical-file-input"
          />
          <Button
            onClick={uploadIcal}
            disabled={!icalFile || icalUploading}
            className="rounded-sm gap-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
            data-testid="ical-upload-btn"
          >
            <Upload className="w-3.5 h-3.5" />
            {icalUploading ? `Mengunggah ${icalProgress}%…` : "Impor"}
          </Button>
        </div>

        {icalUploading && (
          <div className="mt-3 h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
            <div className="h-full bg-[#7C3AED] transition-all" style={{width: `${icalProgress}%`}} />
          </div>
        )}

        {icalResult && (
          <div className="mt-3 text-[12px] bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="ical-result">
            <div className="font-medium text-emerald-800 mb-1">✓ {icalResult.filename}</div>
            <div className="text-emerald-700">
              {icalResult.total_processed?.toLocaleString("id-ID")} event diproses ·
              {" "}<b>{icalResult.inserted?.toLocaleString("id-ID")}</b> baru ·
              {" "}<b>{icalResult.updated?.toLocaleString("id-ID")}</b> diperbarui
            </div>
          </div>
        )}

        {/* Learn duration dari iCal */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span className="text-[12px] font-medium">Belajar Durasi dari iCal ({icalStats?.total_events?.toLocaleString("id-ID") || 0} event)</span>
          </div>
          <p className="text-[11px] text-zinc-500 mb-2">
            AI akan memindai semua event yang sudah di-import, mengklasifikasikan per jenis layanan (CLEANING / AC_REPAIR / AC_INSTALL / CHILLER),
            menghitung durasi aktual (end − start), dan membangun model estimasi yang jauh lebih akurat dari default.
          </p>
          <Button
            variant="outline"
            onClick={learnDurationsIcal}
            disabled={icalLearning || !icalStats?.total_events}
            className="rounded-sm gap-1.5"
            data-testid="ical-learn-btn"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            {icalLearning ? "Sedang belajar…" : "Belajar Durasi dari 43k Event iCal"}
          </Button>
          {icalLearnResult && (
            <div className="mt-3 text-[12px] bg-violet-50 border border-violet-200 rounded-sm p-3" data-testid="ical-learn-result">
              <div className="font-medium text-violet-800 mb-1">
                ✓ {icalLearnResult.events_matched?.toLocaleString("id-ID")} event dipelajari · {icalLearnResult.buckets_saved} bucket estimasi
              </div>
              <div className="text-violet-700 text-[11px] flex flex-wrap gap-x-3 gap-y-0.5">
                {Object.entries(icalLearnResult.by_service_type || {}).map(([k,v]) => (
                  <span key={k}><b>{k}</b>: {v.toLocaleString("id-ID")}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="text-[11px] text-zinc-500 mt-3">
          Tip: cara ekspor kalender Google — buka
          {" "}<a href="https://calendar.google.com/calendar/u/0/r/settings/export" target="_blank" rel="noreferrer"
            className="underline hover:text-[#7C3AED]">Google Calendar Settings → Export</a>,
          {" "}unduh file .zip, lalu upload di sini. Impor bersifat idempotent (upsert per UID) — aman untuk re-upload.
        </div>
      </div>

      <SherlockCorrectionSettings />

      <RoutingEngineStatus />

      {/* v85.25 — Backfill Koordinat WO Lama */}
      <div className="border border-border rounded-sm bg-white p-5" data-testid="backfill-coords-section">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Database className="w-5 h-5 text-amber-700" />
          <h2 className="text-base font-bold tracking-tight">Backfill Koordinat WO Lama</h2>
          <span className="text-[11px] text-zinc-500 ml-1">
            Isi otomatis lat/lng utk WO yg maps_link-nya tanpa koordinat (resolve link + geocode address).
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-3">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Switch checked={bfDryRun} onCheckedChange={setBfDryRun} disabled={bfRunning}
              data-testid="bf-dry-run-toggle" />
            <span className="text-[12px] font-semibold text-zinc-700">Dry-run (preview tanpa update DB)</span>
          </label>

          {!bfRunning ? (
            <Button onClick={runBackfillLoop}
              className="h-9 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bf-start-btn">
              <PlayCircle className="w-3.5 h-3.5 mr-1" />
              {bfDryRun ? "Preview Sekarang" : "Jalankan Backfill"}
            </Button>
          ) : (
            <Button onClick={() => setBfStop(true)} variant="outline"
              className="h-9 border-red-300 text-red-700 hover:bg-red-50"
              data-testid="bf-stop-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" />
              Hentikan
            </Button>
          )}

          {bfRunning && (
            <div className="flex items-center gap-2 text-[12px] text-amber-700">
              <Loader2 className="w-4 h-4 animate-spin" />
              Round #{bfStats.rounds} berjalan…
            </div>
          )}
        </div>

        {/* Live stats */}
        {(bfStats.rounds > 0 || bfRunning) && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <StatBox label="Round" value={bfStats.rounds} color="zinc" />
            <StatBox label="Discan" value={bfStats.scanned} color="zinc" />
            <StatBox label="✓ Dari Link" value={bfStats.from_link} color="emerald" />
            <StatBox label="✓ Dari Alamat" value={bfStats.from_addr} color="emerald" />
            <StatBox label="⚠ Rejected / Gagal" value={`${bfStats.rejected} / ${bfStats.failed}`} color="rose" />
          </div>
        )}

        <div className="text-[10px] text-zinc-500 leading-relaxed">
          • Batching otomatis <b>40 WO per round</b> supaya tidak timeout di ingress.<br />
          • Loop berhenti sendiri saat sisa WO null-coord = 0, atau saat satu round tidak lagi menghasilkan update.<br />
          • Coord di luar service area Banjarbaru akan di-reject (behavior yg benar).<br />
          • Rekomendasi: jalankan <b>Dry-run</b> dulu untuk preview, lalu matikan toggle & klik "Jalankan Backfill" untuk apply.
        </div>
      </div>

      {/* v85.30 — Backfill "Ket: jam HH.MM" dari pesan konfirmasi utk WO QO lama */}
      <div className="border border-border rounded-sm bg-white p-5" data-testid="qo-time-backfill-section">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Brain className="w-5 h-5 text-indigo-700" />
          <h2 className="text-base font-bold tracking-tight">Isi Jam Permintaan ke WO Quick Order Lama</h2>
          <span className="text-[11px] text-zinc-500 ml-1">
            Ekstrak jam dari pesan konfirmasi "Jadwal dikonfirmasi: ... pukul HH:MM", lalu prepend
            "Ket: jam HH.MM" ke description WO. Berguna supaya setelah AI Arranger menggeser,
            admin masih tahu jam permintaan customer.
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Dari tanggal</Label>
            <Input type="date" value={qtFrom} onChange={e => setQtFrom(e.target.value)}
              className="h-9 text-sm" data-testid="qt-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Sampai tanggal</Label>
            <Input type="date" value={qtTo} onChange={e => setQtTo(e.target.value)}
              className="h-9 text-sm" data-testid="qt-date-to" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Switch checked={qtDryRun} onCheckedChange={setQtDryRun} disabled={qtBusy}
              data-testid="qt-dry-run-toggle" />
            <span className="text-[12px] font-semibold text-zinc-700">Dry-run (preview saja)</span>
          </label>
          <Button onClick={runQoTimeBackfill} disabled={qtBusy || !qtFrom || !qtTo}
            className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="qt-start-btn">
            {qtBusy
              ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Memproses…</>
              : <><PlayCircle className="w-3.5 h-3.5 mr-1" /> {qtDryRun ? "Preview" : "Jalankan — Apply"}</>}
          </Button>
        </div>

        {qtResult && (
          <div className="mt-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <StatBox label="Discan (QO)" value={qtResult.total_qo_scanned} color="zinc" />
              <StatBox label="Tanpa Jam" value={qtResult.qo_missing_hint} color="zinc" />
              <StatBox label="✓ Diekstrak" value={qtResult.extracted} color="emerald" />
              <StatBox label={qtResult.dry_run ? "(Preview)" : "✓ Di-update"} value={qtResult.updated} color={qtResult.dry_run ? "zinc" : "emerald"} />
            </div>
            {Array.isArray(qtResult.results) && qtResult.results.length > 0 && (
              <div className="max-h-80 overflow-auto border border-zinc-200 rounded-sm">
                <table className="w-full text-[11px]" data-testid="qt-results-table">
                  <thead className="bg-zinc-100 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Customer</th>
                      <th className="text-left px-2 py-1.5">HP</th>
                      <th className="text-left px-2 py-1.5">Tgl WO</th>
                      <th className="text-left px-2 py-1.5">Scheduled</th>
                      <th className="text-left px-2 py-1.5">Jam Terekstrak</th>
                      <th className="text-left px-2 py-1.5">Sumber</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qtResult.results.map((r, i) => (
                      <tr key={r.wo_id || i} className="border-t border-zinc-100 odd:bg-zinc-50/50"
                        data-testid={`qt-row-${i}`}>
                        <td className="px-2 py-1 font-semibold">{r.customer_name || "—"}</td>
                        <td className="px-2 py-1 font-mono text-zinc-600">{r.customer_phone || "—"}</td>
                        <td className="px-2 py-1 font-mono">{r.wo_date}</td>
                        <td className="px-2 py-1 font-mono text-zinc-500">
                          {(r.current_scheduled_start || "").slice(11, 16) || "—"}
                        </td>
                        <td className="px-2 py-1 font-mono font-bold">
                          {r.extracted_time ? <span className="text-indigo-700">{r.extracted_time}</span> : <span className="text-rose-600">—</span>}
                        </td>
                        <td className="px-2 py-1 text-[10px] text-zinc-500">{r.source || "—"}</td>
                        <td className="px-2 py-1">
                          {r.applied ? <span className="text-emerald-700 font-semibold">✓ applied</span>
                            : r.extracted_time ? <span className="text-zinc-500">preview</span>
                            : <span className="text-rose-600">no match</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="text-[10px] text-zinc-500 leading-relaxed mt-3">
          • Hanya scan WO Quick Order (<code>[via Quick Order Link]</code>) yang belum ada "Ket: jam …" di description.<br />
          • Jam diekstrak dari pesan outbound "Jadwal dikonfirmasi: … YYYY-MM-DD pukul HH:MM"; fallback: pesan inbound customer menyebut "jam HH" / "pukul HH".<br />
          • Jalankan <b>Preview</b> dulu untuk inspeksi — apply hanya setelah Anda yakin.
        </div>
      </div>

      {/* v85.26 — Cleanup WO SCHEDULED > N hari lalu → DONE */}
      <div className="border border-border rounded-sm bg-white p-5" data-testid="cleanup-stale-section">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <CheckCircle2 className="w-5 h-5 text-emerald-700" />
          <h2 className="text-base font-bold tracking-tight">Rapihkan WO Lama (SCHEDULED → DONE)</h2>
          <span className="text-[11px] text-zinc-500 ml-1">
            Set otomatis status WO yang masih SCHEDULED tapi tanggalnya sudah lewat jadi DONE.
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Threshold (hari yang lalu)
            </Label>
            <Input
              type="number" min={1} max={90}
              value={cuDays}
              onChange={e => setCuDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 3)))}
              className="h-9 w-24 text-sm font-mono"
              data-testid="cu-days-input"
              disabled={cuBusy}
            />
          </div>
          <Button
            onClick={() => runCleanupStale(true)}
            variant="outline"
            className="h-9"
            disabled={cuBusy}
            data-testid="cu-dry-run-btn">
            {cuBusy
              ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Memproses…</>
              : <>Preview (dry-run)</>}
          </Button>
          <Button
            onClick={() => runCleanupStale(false)}
            className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={cuBusy}
            data-testid="cu-apply-btn">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            Jalankan — Set DONE
          </Button>
        </div>

        {cuResult && (
          <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/40 text-[11px]">
            <div className="font-bold mb-1">
              {cuResult.dry_run ? "Preview" : "Hasil"} —
              cutoff <span className="font-mono">{(cuResult.cutoff || "").slice(0, 10)}</span>
            </div>
            <div className="mb-2">
              <b>{cuResult.matched}</b> WO cocok ·
              {cuResult.dry_run
                ? <> akan di-set DONE</>
                : <> <b>{cuResult.modified}</b> ter-update</>}
            </div>
            {(cuResult.samples || []).length > 0 && (
              <>
                <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">
                  Sample ({cuResult.samples.length} tertua)
                </div>
                <div className="font-mono text-[10px] leading-relaxed">
                  {cuResult.samples.map((s, i) => (
                    <div key={i}>
                      {(s.scheduled_start || "").slice(0, 16)} · {s.service_type || "-"} · {s.customer_name || "-"}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="text-[10px] text-zinc-500 leading-relaxed mt-3">
          • Operasi idempotent — aman dijalankan berulang.<br />
          • WO yang sudah DONE/CANCELLED tidak akan di-sentuh.<br />
          • Rekomendasi: klik <b>Preview</b> dulu untuk lihat berapa WO yang akan di-update.
        </div>
      </div>

      {/* v85.25 — AI WO Arranger */}
      <div className="border border-border rounded-sm bg-white p-5" data-testid="ai-arranger-section">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Brain className="w-5 h-5 text-indigo-700" />
          <h2 className="text-base font-bold tracking-tight">AI WO Arranger</h2>
          <span className="text-[11px] text-zinc-500 ml-1">
            AI agent untuk mengoptimalkan susunan WO harian berdasarkan Distance Matrix.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${
              aiCfg.enabled ? "text-emerald-700" : "text-zinc-500"
            }`}>
              {aiCfg.enabled ? "● Aktif" : "⏸ Nonaktif"}
            </span>
            <Switch
              checked={!!aiCfg.enabled}
              onCheckedChange={saveAiToggle}
              disabled={aiBusy}
              data-testid="ai-arranger-toggle"
            />
          </div>
        </div>
        {aiCfg.updated_at && (
          <div className="text-[10px] text-zinc-400 mb-3">
            Terakhir diubah {(aiCfg.updated_at || "").slice(0, 16).replace("T", " ")}
            {aiCfg.updated_by ? ` oleh ${aiCfg.updated_by}` : ""}
          </div>
        )}

        {/* v85.27 — Model info (read-only; edit di halaman LLM Provider) */}
        <div className="mb-4 p-2.5 bg-zinc-50 border border-zinc-200 rounded-sm text-[11px] flex items-center justify-between gap-2">
          <div>
            <span className="text-zinc-500 uppercase tracking-widest text-[10px] font-bold mr-2">
              Model
            </span>
            <span className="font-mono text-zinc-800">
              {aiCfg.openrouter_model || "—"}
            </span>
          </div>
          <a href="/llm-settings" className="text-indigo-700 hover:underline text-[11px]">
            Ubah di LLM Provider →
          </a>
        </div>

        {/* Manual run */}
        <div className="border border-zinc-200 rounded-sm p-3 mb-4 bg-zinc-50/40">
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">
            Jalankan Manual (termasuk hari ini)
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Dari</Label>
              <Input type="date" value={aiRunFrom} onChange={e => setAiRunFrom(e.target.value)}
                className="h-9 text-sm" data-testid="ai-arranger-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Sampai (opsional)</Label>
              <Input type="date" value={aiRunTo} onChange={e => setAiRunTo(e.target.value)}
                className="h-9 text-sm" data-testid="ai-arranger-date-to" />
            </div>
            <Button onClick={runAiNow} disabled={aiRunning || !aiRunFrom}
              className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="ai-arranger-run-now">
              {aiRunning
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Memproses…</>
                : <><PlayCircle className="w-3.5 h-3.5 mr-1" /> Jalankan Sekarang</>}
            </Button>
          </div>
          {aiRunResult && (
            <div className="mt-3 text-[11px] text-zinc-700 bg-white border border-zinc-200 rounded-sm p-2">
              <div className="font-bold mb-1">Hasil ({aiRunResult.total_days} hari)</div>
              {(aiRunResult.results || []).map((r, i) => (
                <div key={i} className="font-mono">
                  <b>{r.date}</b>: moved={r.moved ?? 0} · classified={r.classified ?? 0}
                  {Array.isArray(r.skipped_no_coord) && r.skipped_no_coord.length > 0 &&
                    ` · skip_no_coord=${r.skipped_no_coord.length}`}
                  {r.note && ` · ${r.note}`}
                  {r.skipped && ` · ${r.reason}`}
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-zinc-500 mt-2">
            Catatan: kalau toggle OFF, agent tidak jalan di sini maupun otomatis.
            Kalau toggle ON tanpa rentang manual, agent otomatis jalan untuk tanggal besok ke depan
            setiap kali ada WO baru/diubah.
          </div>
        </div>

        {/* System prompt editor */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[11px] uppercase tracking-widest">System Prompt</Label>
            <Button variant="outline" size="sm" onClick={saveAiPrompt} disabled={aiBusy}
              data-testid="ai-arranger-prompt-save">
              <Save className="w-3.5 h-3.5 mr-1" /> Simpan Prompt
            </Button>
          </div>
          <Textarea
            value={aiPromptDraft}
            onChange={(e) => setAiPromptDraft(e.target.value)}
            rows={12}
            className="font-mono text-[11px] leading-relaxed"
            data-testid="ai-arranger-prompt-textarea"
          />
          <div className="text-[10px] text-zinc-500 mt-1">
            Perubahan efektif seketika, tidak perlu rebuild. Dipakai sebagai referensi
            logika penempatan (mapping teknisi, prioritas Garansi, batasan ±30 menit, dsb.)
          </div>
        </div>

        {/* Activity log */}
        <div className="border border-zinc-200 rounded-sm overflow-x-auto"
          data-testid="ai-arranger-logs">
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center justify-between">
            <span>Log Aktivitas (20 terakhir)</span>
            <Button variant="ghost" size="sm" className="h-6 text-[10px]"
              onClick={async () => {
                const l = await api.aiArrangerLogs(20);
                setAiLogs(l.items || []);
                toast.success("Log di-refresh");
              }}>
              Refresh
            </Button>
          </div>
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-50/40 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-2 py-1.5">Waktu</th>
                <th className="px-2 py-1.5">Tgl WO</th>
                <th className="px-2 py-1.5">WO ID</th>
                <th className="px-2 py-1.5">Dari</th>
                <th className="px-2 py-1.5">Ke</th>
                <th className="px-2 py-1.5">Alasan</th>
              </tr>
            </thead>
            <tbody>
              {aiLogs.length === 0 && (
                <tr><td colSpan={6} className="text-center text-zinc-400 py-3">Belum ada log</td></tr>
              )}
              {aiLogs.map((l, i) => (
                <tr key={i} className="border-b border-zinc-100 last:border-0">
                  <td className="px-2 py-1.5 font-mono text-[10px]">
                    {(l.arranged_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-2 py-1.5 font-mono">{l.date}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{(l.wo_id || "").slice(0, 8)}</td>
                  <td className="px-2 py-1.5">
                    <span className="font-mono text-[10px]">
                      {(l.previous_team || "-").slice(0, 8)} · {l.previous_time || "-"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="font-mono text-[10px] text-emerald-700 font-bold">
                      {(l.new_team || "-").slice(0, 8)} · {l.new_time || "-"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-zinc-600">{l.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
